import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { Button } from '../components/ui/Button';
import { LiveMark } from '../components/ui/LiveMark';
import { api, type SessionView } from '../lib/api';
import { relativeWithAbsolute } from '../lib/format';
import { useSession } from '../lib/session';

/**
 * You → Security (M5.5): the person's sessions (device, created, last seen, this device) and
 * two actions: sign out of other devices, sign out everywhere. Rules and whitespace.
 */
export function SecurityScreen() {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? 'en';
  const { me, setMe } = useSession();
  const navigate = useNavigate();
  const timeZone = me?.timezone ?? 'Asia/Qatar';
  const [sessions, setSessions] = useState<SessionView[] | null>(null);
  const [busy, setBusy] = useState<'others' | 'all' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .sessions()
      .then((res) => {
        if (!cancelled) setSessions(res.sessions);
      })
      .catch(() => {
        if (!cancelled) setSessions([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const revoke = async (includeCurrent: boolean) => {
    setBusy(includeCurrent ? 'all' : 'others');
    setError(null);
    try {
      const res = await api.revokeSessions(includeCurrent);
      if (includeCurrent) {
        setMe(null);
        navigate('/signin', { replace: true });
        return;
      }
      setDone(res.revoked);
      setSessions((prev) => (prev ?? []).filter((s) => s.current));
    } catch {
      setError(t('common.error'));
    } finally {
      setBusy(null);
    }
  };

  const others = (sessions ?? []).filter((s) => !s.current).length;

  return (
    <div className="mt-4 flex flex-col gap-4">
      {sessions === null ? (
        <div className="flex items-center gap-3 py-2" role="status">
          <LiveMark state="thinking" size={16} title={t('common.loading')} />
        </div>
      ) : (
        <section aria-label={t('security.sessions')}>
          <h3 className="mb-1 text-sm font-semibold text-ink-2">{t('security.sessions')}</h3>
          <ul className="divide-y divide-line" data-sessions>
            {sessions.map((s) => (
              <li key={s.session_id} className="flex items-start justify-between gap-3 py-3">
                <div className="min-w-0">
                  <p className="text-base text-ink" dir="ltr">
                    {s.device}
                    {s.current ? (
                      <span className="ms-2 rounded-full bg-brand-soft px-2 py-0.5 text-xs text-ink">
                        {t('security.thisDevice')}
                      </span>
                    ) : null}
                  </p>
                  <p className="tabular mt-0.5 text-xs text-ink-3">
                    {t('security.created')} {relativeWithAbsolute(s.created_at, locale, timeZone)}
                  </p>
                  <p className="tabular text-xs text-ink-3">
                    {t('security.lastSeen')}{' '}
                    {relativeWithAbsolute(s.last_seen_at, locale, timeZone)}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}
      {done !== null ? (
        <p role="status" className="text-sm text-ink-2" data-revoked>
          {t('security.signedOutOthers', { count: done })}
        </p>
      ) : null}
      {error ? (
        <p role="alert" className="text-sm text-critical-text">
          {error}
        </p>
      ) : null}
      <div className="flex flex-col gap-2">
        <Button
          variant="secondary"
          loading={busy === 'others'}
          disabled={busy !== null || others === 0}
          onClick={() => void revoke(false)}
        >
          {t('security.signOutOthers')}
        </Button>
        <Button
          variant="critical"
          loading={busy === 'all'}
          disabled={busy !== null}
          onClick={() => void revoke(true)}
        >
          {t('security.signOutEverywhere')}
        </Button>
      </div>
    </div>
  );
}
